import type { CustomProduct, ProductGroup } from "@apptus/esales-api";
import type { Variant } from "types";

import type { MarketConfiguration } from "@/config/markets/const";
import {
  flattenCustomAttributes,
  flattenCustomJson,
  flattenCustomLengths,
  flattenCustomNumbers,
  flattenVariantAttributes,
  getImagesOfProduct,
  getPriceFromVariant,
} from "./utils";

export function elevateProductToNgVariant({
  product,
  marketConfig,
}: {
  product: CustomProduct;
  marketConfig: MarketConfiguration;
}): Variant {
  const {
    variants: [variant],
  } = product;
  const images = getImagesOfProduct(product, marketConfig);
  const pricing = getPriceFromVariant(product, marketConfig);
  const variantAttributes = flattenVariantAttributes(product);

  const numbers = flattenCustomNumbers(product, marketConfig);
  const lengths = flattenCustomLengths(product, marketConfig);
  const custom = flattenCustomAttributes(product, marketConfig, true);
  const json = flattenCustomJson(product);
  const items = json.structureItems?.map((item) => ({
    partNo: item.PartNo ?? "",
    quantity: item.Quantity ?? 0,
    order: item.SortOrder ?? 0,
  }));

  const [image] = images;

  return {
    ...custom,
    ...numbers,
    ...lengths,
    ...json,
    ...variantAttributes,
    campaignPrice: pricing.price < pricing.price30,
    price: pricing.price,
    priceType: pricing.priceType,
    originalPrice: pricing.price30,
    rrpWhenCampaign: pricing.priceRrp,
    id: variant.key,
    ticket: variant.ticket,
    type: "Variant",
    title: json.variantName ?? product.title,
    image,
    images,
    groupId: product.key,
    nextDeliveryDate: new Date(custom.nextDeliveryDate ?? ""),
    stock: numbers.stockOnHand === undefined || numbers.stockOnHand >= 1,
    stockQuantity: numbers.stockOnHand,
    articleStatus: custom.itemStatus ?? "",
    stockStatus: custom.stockStatus ?? "",
    lightsourceQuantity: numbers.norce_light_source_quantity,
    flags: product.custom?.variantFlags?.map((flag) => flag.id),
    VariantDefiningAttributes: JSON.stringify(
      json.variantDefiningVariantAttributes,
    ),
    deliveryTimeDays: numbers.delivery_time_days,
    rrpWHenCampaign: numbers.rrp,
    vatRate: numbers.vat,
    link: variant.link,
    ProductURL: variant.link,
    category: custom.totalCategoryCodes,
    items,
  };
}

export function mapElevateVariant({
  product,
  marketConfig,
}: {
  product: CustomProduct;
  marketConfig: MarketConfiguration;
}) {
  return elevateProductToNgVariant({
    product,
    marketConfig,
  });
}

export function mapElevateVariants(
  productGroup: ProductGroup,
  marketConfig: MarketConfiguration,
) {
  return productGroup.products.map((product) =>
    mapElevateVariant({
      product: product as CustomProduct,
      marketConfig,
    }),
  );
}
